// v7.56 — Creative Layers controls, rendered in the right dock while a tile is
// in Layers mode: the layer stack (top first) with select / raise / lower /
// delete, and Save. Closing the panel (unmount) exits Layers mode on the tile.
import { useEffect } from 'react';
import { ArrowUp, ArrowDown, Trash2, Check, Loader2, Layers, Combine, ImageDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { LAYERS_EXPORT_MARKER, useCreativeLayersStore } from './creative-layers-store';
import { useCanvasStore } from './canvas-store';

const CHECKER = 'repeating-conic-gradient(#e9e6e0 0 25%, #f7f5f0 0 50%) 0 0 / 12px 12px';

export function CreativeLayersPanel() {
  const activeImageId = useCreativeLayersStore((s) => s.activeImageId);
  const doc = useCreativeLayersStore((s) => (s.activeImageId ? s.prepares[s.activeImageId]?.doc ?? null : null));
  const phase = useCreativeLayersStore((s) => (s.activeImageId ? s.prepares[s.activeImageId]?.phase ?? 'preparing' : 'preparing'));
  const phaseNote = useCreativeLayersStore((s) => (s.activeImageId ? s.prepares[s.activeImageId]?.phaseNote ?? '' : ''));
  const preparingOthers = useCreativeLayersStore((s) =>
    Object.entries(s.prepares).filter(([id, p]) => p.phase === 'preparing' && id !== s.activeImageId).length);
  const selectedLayerId = useCreativeLayersStore((s) => s.selectedLayerId);
  const selectedLayerIds = useCreativeLayersStore((s) => s.selectedLayerIds);
  const grouping = useCreativeLayersStore((s) => s.grouping);
  const saving = useCreativeLayersStore((s) => s.saving);
  const isEditedCopy = useCreativeLayersStore((s) => !!(s.activeImageId && (s.prepares[s.activeImageId]?.sourceUrl ?? '').includes(LAYERS_EXPORT_MARKER)));
  const { setSelected, toggleSelected, reorderLayer, deleteLayer, saveToCanvas, mergeSelected, bakeSelected, close } = useCreativeLayersStore.getState();
  const { toast } = useToast();

  // Panel unmount = user closed the panel (or switched panels) → exit Layers
  // mode so the tile goes back to its normal image.
  useEffect(() => () => { useCreativeLayersStore.getState().close(); }, []);

  const handleSave = async () => {
    const result = await saveToCanvas();
    if (result === 'updated') {
      toast({ title: 'Changes saved', description: 'This creative was updated in place.' });
    } else if (result === 'copy') {
      toast({ title: 'Saved to canvas', description: 'Rearranged copy placed next to the original.' });
      close();
      useCanvasStore.getState().closeSidebarPanel();
    } else {
      toast({ title: 'Save failed', description: result, variant: 'destructive' });
    }
  };

  const handleGroup = async (kind: 'merge' | 'bake') => {
    const err = kind === 'merge' ? await mergeSelected() : await bakeSelected();
    if (err) toast({ title: kind === 'merge' ? 'Merge failed' : 'Bake failed', description: err, variant: 'destructive' });
    else toast({ title: kind === 'merge' ? 'Pieces merged' : 'Baked into background', description: kind === 'merge' ? 'They now move as one.' : 'Those pieces are part of the background now.' });
  };

  const iconBtn = (disabled = false): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 26, borderRadius: 6, cursor: disabled ? 'default' : 'pointer',
    border: '1px solid #DCE3ED', background: '#FFFFFF', color: disabled ? '#94A3B8' : '#6B7280',
  });

  if (phase !== 'ready' || !doc || !activeImageId) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center', color: '#5F6D82', fontSize: 13 }}>
        {phase === 'failed' ? 'Splitting the creative failed — close and retry.' : (phaseNote || 'Splitting the creative into layers…')}
        {phase !== 'failed' && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#5F6D82' }}>
            Usually 3–5 minutes. You can keep working on the canvas meanwhile.
          </div>
        )}
        {preparingOthers > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#5F6D82' }}>
            {preparingOthers} more creative{preparingOthers === 1 ? '' : 's'} splitting in the background.
          </div>
        )}
      </div>
    );
  }

  const stack = [...doc.layers].sort((a, b) => b.z - a.z); // top of stack first
  const selected = doc.layers.find((l) => l.id === selectedLayerId) ?? null;

  return (
    <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14, height: '100%', boxSizing: 'border-box', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1A1A1A' }}>
        <Layers size={16} />
        <div style={{ fontSize: 14, fontWeight: 600 }}>Layers</div>
        <div style={{ fontSize: 12, color: '#5F6D82', marginLeft: 'auto' }}>{doc.layers.length} pieces</div>
      </div>
      <div style={{ fontSize: 12, color: '#5F6D82', lineHeight: 1.45 }}>
        Drag any piece on the tile to move it; corner handles resize and rotate. Shift-click to select several, then merge them into one piece or bake them into the background.
      </div>

      {selectedLayerIds.length > 0 && (
        <div data-testid="creative-layers-group-actions" style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => handleGroup('merge')}
            disabled={selectedLayerIds.length < 2 || !!grouping}
            data-testid="creative-layers-merge"
            title={selectedLayerIds.length < 2 ? 'Shift-click a second piece to merge' : 'Merge the selected pieces into one'}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: selectedLayerIds.length < 2 || grouping ? 'default' : 'pointer', border: '1px solid #6366F1', background: 'rgba(99,102,241,0.08)', color: '#6366F1', opacity: selectedLayerIds.length < 2 ? 0.5 : 1 }}
          >
            {grouping === 'merge' ? <Loader2 size={13} className="animate-spin" /> : <Combine size={13} />}
            Merge {selectedLayerIds.length > 1 ? selectedLayerIds.length : ''}
          </button>
          <button
            onClick={() => handleGroup('bake')}
            disabled={!!grouping}
            data-testid="creative-layers-bake"
            title="Flatten the selected pieces into the background"
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: grouping ? 'default' : 'pointer', border: '1px solid #DCE3ED', background: '#FFFFFF', color: '#1A1A1A' }}
          >
            {grouping === 'bake' ? <Loader2 size={13} className="animate-spin" /> : <ImageDown size={13} />}
            Bake into background
          </button>
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        data-testid="creative-layers-save"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: '10px 12px', borderRadius: 10, border: 'none', cursor: saving ? 'default' : 'pointer',
          background: '#1A1A1A', color: '#fff', fontSize: 13, fontWeight: 600, opacity: saving ? 0.7 : 1,
        }}
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
        {saving ? 'Saving…' : isEditedCopy ? 'Save changes' : 'Save to canvas'}
      </button>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="creative-layers-list">
        {stack.map((l, i) => {
          const isSel = selectedLayerIds.includes(l.id);
          return (
            <div
              key={l.id}
              onClick={(e) => { if (e.shiftKey || e.metaKey || e.ctrlKey) toggleSelected(l.id); else setSelected(isSel && selectedLayerIds.length === 1 ? null : l.id); }}
              data-testid={`creative-layer-row-${l.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: 6, borderRadius: 10, cursor: 'pointer',
                border: '1px solid ' + (isSel ? '#6366F1' : '#DCE3ED'),
                background: isSel ? 'rgba(99,102,241,0.08)' : '#FFFFFF',
              }}
            >
              <div style={{ width: 44, height: 44, borderRadius: 6, background: CHECKER, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                <img src={l.url} alt="" style={{ maxWidth: 40, maxHeight: 40, objectFit: 'contain' }} draggable={false} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name || `Layer ${l.z}`}</div>
                <div style={{ fontSize: 11, color: '#5F6D82' }}>{i === 0 ? 'Top' : i === stack.length - 1 ? 'Bottom' : `${stack.length - i} of ${stack.length}`}</div>
              </div>
              <button title="Bring forward" disabled={i === 0} onClick={(e) => { e.stopPropagation(); reorderLayer(l.id, 'up'); }} style={iconBtn(i === 0)}><ArrowUp size={13} /></button>
              <button title="Send backward" disabled={i === stack.length - 1} onClick={(e) => { e.stopPropagation(); reorderLayer(l.id, 'down'); }} style={iconBtn(i === stack.length - 1)}><ArrowDown size={13} /></button>
              <button title="Delete piece" onClick={(e) => { e.stopPropagation(); deleteLayer(l.id); }} style={{ ...iconBtn(false), color: '#EF4444' }}><Trash2 size={13} /></button>
            </div>
          );
        })}
        {stack.length === 0 && (
          <div style={{ fontSize: 12, color: '#5F6D82', textAlign: 'center', padding: 12 }}>All pieces removed — only the background remains.</div>
        )}
      </div>

      {selected && (
        <div style={{ fontSize: 11, color: '#5F6D82' }}>
          {selected.name}: {Math.round(selected.w)}×{Math.round(selected.h)} px at {Math.round(selected.x)}, {Math.round(selected.y)}
          {selected.rotation ? ` · ${selected.rotation}°` : ''}
        </div>
      )}

      {preparingOthers > 0 && (
        <div style={{ fontSize: 12, color: '#5F6D82' }}>
          {preparingOthers} more creative{preparingOthers === 1 ? '' : 's'} splitting in the background — click their tile when done.
        </div>
      )}
    </div>
  );
}
